import { useState, useEffect } from 'react';
import { ImageOff } from 'lucide-react';
import { usePhotoStore } from '../../store';

interface PhotoThumbnailProps {
  photoId?: string;
  photo?: any;
  src?: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

/**
 * Photo thumbnail component that loads thumbnails from server via signed URLs
 */
export function PhotoThumbnail({ photoId, photo, src, alt, className = '', onClick }: PhotoThumbnailProps) {
  const id = photoId || photo?.id;
  const [imageSrc, setImageSrc] = useState<string | null>(src || photo?.thumbnailUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { getPhotoUrl, getPhotoById } = usePhotoStore();

  // Load thumbnail URL from server if not provided
  useEffect(() => {
    const loadThumbnail = async () => {
      // If src is provided and valid, use it
      if (src) {
        setImageSrc(src);
        return;
      }

      // Check if photo has cached thumbnail URL
      if (!id) return;
      const photoData = getPhotoById(id);
      if (photoData?.thumbnailUrl) {
        setImageSrc(photoData.thumbnailUrl);
        return;
      }

      // Load from server
      setIsLoading(true);
      try {
        const url = await getPhotoUrl(id!, true); // true = thumbnail
        if (url) {
          setImageSrc(url);
          setHasError(false);
        } else {
          setHasError(true);
        }
      } catch (e) {
        console.warn('Failed to load thumbnail:', e);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadThumbnail();
  }, [photoId, src, getPhotoUrl, getPhotoById]);

  const handleError = async () => {
    // If already loading or already has error, don't retry
    if (isLoading || hasError) return;
    
    setIsLoading(true);
    
    try {
      // Retry loading from server
      if (!id) return;
      const url = await getPhotoUrl(id!, true);
      if (url) {
        setImageSrc(url);
        setHasError(false);
      } else {
        setHasError(true);
      }
    } catch (e) {
      console.warn('Failed to reload thumbnail:', e);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasError) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-700 ${className}`}
        onClick={onClick}
        title={alt}
      >
        <ImageOff className="w-4 h-4 text-gray-500" />
      </div>
    );
  }

  if (isLoading || !imageSrc) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-800 ${className}`}
        onClick={onClick}
        title={alt}
      >
        <div className="w-4 h-4 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={handleError}
      loading="lazy"
    />
  );
}
